import { useEffect, useRef } from "react";

// Decorative geometry shared by the site mark, signal field, and section rules.
export default function Constellation({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 320 280" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true" focusable="false">
      <path d="M45 170 104 62 178 188 269 82M45 170l133 18M104 62l165 20M178 188l-23 53" />
      <g fill="currentColor" stroke="none">
        <circle cx="45" cy="170" r="5" />
        <circle cx="104" cy="62" r="5" />
        <circle cx="178" cy="188" r="5" />
        <circle cx="269" cy="82" r="5" />
        <circle cx="155" cy="241" r="5" />
      </g>
    </svg>
  );
}

export function ConstellationDivider() {
  return <div className="constellation-divider" aria-hidden="true"><Constellation /></div>;
}


// Discovery (0–10s), propagation (8–25s), optimisation (26–50s), then orbit.
const count = 24;
const nodes = Array.from({ length: count }, (_, i) => ({
  angle: i * Math.PI * 2 / count,
  seed: (i * 0.618034) % 1,
}));
const edges = nodes.flatMap((_, to) => nodes.slice(0, to).map((__, from) => ({
  from, to, branch: from === Math.floor((to - 1) / 2),
})));
const smooth = (value) => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
};
const mix = (a, b, t) => a + (b - a) * t;
// A small ignition stutter, then a steady connection.
const ignite = (age) => age < 0 ? 0 : age < 0.3 ? 0.6 : age < 0.65 ? 0.08 : 1;

export function LivingConstellation() {
  const field = useRef(null);

  useEffect(() => {
    const svg = field.current;
    const hero = svg.closest(".hero");
    const cover = hero.querySelector(".hero__cover");
    const circles = svg.querySelectorAll("circle");
    const lines = svg.querySelectorAll(".living-constellation__edge");
    const roots = svg.querySelectorAll(".living-constellation__root");
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let visible = true;
    let elapsed = 0;
    let previous = null;

    const draw = (time) => {
      // Pixel coordinates keep the orbit circular, even in a tall mobile hero.
      const area = svg.getBoundingClientRect();
      const width = cover.offsetWidth;
      const height = cover.offsetHeight;
      if (!area.width || !width) return;
      // Use layout offsets so the entrance reveal cannot displace the final centre.
      let cx = width / 2;
      let cy = height / 2;
      for (let element = cover; element && element !== hero; element = element.offsetParent) {
        cx += element.offsetLeft;
        cy += element.offsetTop;
      }
      const radius = Math.hypot(width, height) / 2 + Math.min(26, area.width * 0.045);
      const order = smooth((time - 26) / 24);
      const rotation = Math.max(0, time - 50) * Math.PI / 120;
      svg.dataset.phase = time < 10 ? "discovery" : time < 26 ? "propagation" : time < 50 ? "optimisation" : "orbit";
      const positions = nodes.map((node, i) => {
        const scatterAngle = node.seed * Math.PI * 2;
        const spread = 0.65 + ((i * 0.37) % 1) * 0.8;
        const drift = time / (13 + i % 7) + node.angle;
        const x = area.width < 961
          ? cx + Math.cos(scatterAngle) * Math.min(radius * spread, area.width * 0.43)
          : area.width * (0.12 + node.seed * 0.8);
        const y = area.width < 961
          ? cy + Math.sin(scatterAngle) * radius * spread
          : area.height * (0.19 + ((i * i * 0.137 + i * 0.271) % 1) * 0.64);
        const px = mix(x + Math.sin(drift) * 18, cx + Math.cos(node.angle + rotation) * radius, order);
        const py = mix(y + Math.cos(drift * 0.83 + i) * 15, cy + Math.sin(node.angle + rotation) * radius, order);
        const opacity = smooth((time - node.seed * 7) / 3);
        circles[i].setAttribute("cx", px);
        circles[i].setAttribute("cy", py);
        circles[i].setAttribute("r", mix(2.5 + i % 3, 3.4, order));
        circles[i].setAttribute("opacity", opacity * 0.85);
        return { x: px, y: py };
      });
      const setLine = (line, a, b, opacity) => {
        line.setAttribute("x1", a.x);
        line.setAttribute("y1", a.y);
        line.setAttribute("x2", b.x);
        line.setAttribute("y2", b.y);
        line.setAttribute("opacity", opacity);
      };
      // The first three branches emerge from behind the cover, then fan out.
      roots.forEach((line, i) => setLine(line, { x: cx, y: cy }, positions[i], ignite(time - 8 - i) * (1 - order) * 0.48));
      edges.forEach((edge, i) => {
        const arrival = edge.branch ? 10 + edge.to * 0.58 : 32 + ((i * 0.618034) % 1) * 16;
        const strength = edge.branch ? mix(0.48, 0.19, order) : 0.19;
        setLine(lines[i], positions[edge.from], positions[edge.to], ignite(time - arrival) * strength);
      });
    };
    const tick = (now) => {
      if (previous !== null) elapsed += Math.min(now - previous, 100) / 1000;
      previous = now;
      draw(elapsed);
      frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      cancelAnimationFrame(frame);
      previous = null;
      if (motion.matches) draw(50);
      else if (visible && !document.hidden) frame = requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      sync();
    });
    const resize = new ResizeObserver(() => draw(motion.matches ? 50 : elapsed));
    resize.observe(hero);
    resize.observe(cover);
    draw(motion.matches ? 50 : 0);
    observer.observe(svg);
    motion.addEventListener("change", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      resize.disconnect();
      motion.removeEventListener("change", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);

  return (
    <svg ref={field} className="living-constellation" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true" focusable="false">
      {[0, 1, 2].map(i => <line key={i} className="living-constellation__root" opacity="0" />)}
      {edges.map(({ from, to }) => <line key={`${from}-${to}`} className="living-constellation__edge" opacity="0" />)}
      {nodes.map((_, i) => <circle key={i} r="3.4" fill="currentColor" stroke="none" opacity="0" />)}
    </svg>
  );
}
